import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut, Calendar, MapPin, FileText, Award, User, X, Loader2, Download, Home,
  CalendarCheck, RefreshCw, AlertTriangle, History, CalendarPlus, Camera,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import api, { formatApiError } from "../../lib/api";
import { formatDate as fmt } from "../../lib/date";
import { useAuth } from "../../context/AuthContext";
import { useContent } from "../../context/ContentContext";
import HeroBackdrop from "../../components/HeroBackdrop";
import { MessageCircle } from "lucide-react";
import { GuideCard, downloadGuide } from "../../components/GuideCard";
import { LayoutDashboard } from "lucide-react";
import { Mail } from "lucide-react";
import MemberExtras from "./MemberExtras";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolvePhoto = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

const GRADE_COLOR = { gold: "#D97706", silver: "#64748B", bronze: "#B45309", none: "#94A3B8" };
const RECERT = {
  overdue: { color: "#DC2626", bg: "#FEF2F2", border: "#FECACA", label: "Overdue" },
  due_soon: { color: "#D97706", bg: "#FFFBEB", border: "#FDE68A", label: "Due soon" },
  ok: { color: "#059669", bg: "#ECFDF5", border: "#A7F3D0", label: "Up to date" },
  none: { color: "#64748B", bg: "#F1F5F9", border: "#E2E8F0", label: "Not yet graded" },
};

const addYears = (iso, n) => {
  const d = new Date(iso);
  d.setFullYear(d.getFullYear() + n);
  return d.toISOString().slice(0, 10);
};

export default function MemberDashboard() {
  const { user, logout, refreshUser, isAdmin } = useAuth();
  const { t } = useContent();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [dmUnread, setDmUnread] = useState(0);
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoInput = useRef(null);

  const load = () => {
    Promise.all([api.get("/me/bookings"), api.get("/documents")])
      .then(([b, d]) => { setBookings(b.data); setDocs(d.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/chat/unread").then(({ data }) => setUnreadTotal(Object.values(data).reduce((a, b) => a + b, 0))).catch(() => {});
    api.get("/chat/members").then(({ data }) => setDmUnread(data.reduce((a, m) => a + (m.unread || 0), 0))).catch(() => {});
  };
  useEffect(load, []);

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Profile photo updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't upload photo"); }
    finally { setPhotoBusy(false); if (photoInput.current) photoInput.current.value = ""; }
  };

  const removePhoto = async () => {
    setPhotoBusy(true);
    try { await api.delete("/me/photo"); await refreshUser(); toast.success("Photo removed"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPhotoBusy(false); }
  };

  const cancel = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking cancelled.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadDoc = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_filename || `${d.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("This document isn't available to download yet.");
    }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const addToCalendar = (b) => {
    const ev = b.event;
    if (!ev) return;
    const dt = (ev.date || "").replace(/-/g, "");
    const tm = (ev.time || "").replace(":", "");
    const start = dt && tm.length === 4 ? `${dt}T${tm}00` : dt ? `${dt}T090000` : "";
    if (!start) return;
    const stamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const ics = [
      "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//RoSPA WM//EN", "BEGIN:VEVENT",
      `UID:${b.id}@rospawm`, `DTSTAMP:${stamp}`, `DTSTART:${start}`,
      `SUMMARY:${ev.title || "Event"}`, `LOCATION:${(ev.location || "").replace(/,/g, "\\,")}`,
      `DESCRIPTION:${(ev.description || "").replace(/\n/g, " ").replace(/,/g, "\\,")}`,
      "END:VEVENT", "END:VCALENDAR",
    ].join("\r\n");
    const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(ev.title || "event").replace(/\s+/g, "-")}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const rc = RECERT[user.recert_status] || RECERT.none;
  const history = [...(user.grade_history || [])].sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="member-dashboard">
      <DashHeader onLogout={doLogout} unreadTotal={unreadTotal + dmUnread} isAdmin={isAdmin} canMessage={!!(user.is_tutor || user.is_owner || ["admin", "committee"].includes(user.role))} />

      <div className="relative overflow-hidden bg-[#0F2744]">
        <HeroBackdrop image={t("img.member", "https://customer-assets-39nsmqrw.emergentagent.net/job_rospa-wm-portal/artifacts/db9qeg0a_image.png")} opacity={0.35} />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-wrap items-center gap-4">
          <div className="relative group shrink-0">
            {user.photo ? (
              <img src={resolvePhoto(user.photo)} alt={user.name} className="h-16 w-16 rounded-2xl object-cover" data-testid="member-avatar-photo" />
            ) : (
              <div className="h-16 w-16 rounded-2xl bg-white/10 backdrop-blur text-white flex items-center justify-center font-heading font-bold text-xl">
                {user.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
              </div>
            )}
            <input ref={photoInput} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="member-photo-input" />
            <button onClick={() => photoInput.current?.click()} disabled={photoBusy}
              className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-amber-500 text-[#0F2744] flex items-center justify-center shadow hover:bg-amber-400 disabled:opacity-50"
              title="Change photo" data-testid="member-photo-upload-btn">
              {photoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
            </button>
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-white">Welcome, {user.name.split(" ")[0]}</h1>
            <p className="text-slate-300 capitalize">{user.member_status} · {user.pathway} pathway</p>
            {user.photo && (
              <button onClick={removePhoto} disabled={photoBusy} className="text-xs text-slate-300 hover:text-white underline mt-1" data-testid="member-photo-remove-btn">Remove photo</button>
            )}
          </div>
          <button
            onClick={() => downloadGuide("/me/guides/member", "rospa-wm-member-guide.pdf").catch(() => toast.error("Couldn't download the guide. Please try again."))}
            className="ml-auto inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-bold text-[#0F2744] shadow hover:bg-amber-400 transition-colors"
            data-testid="member-guide-download-header">
            <Download className="h-4 w-4" /> Download the Member Guide (PDF)
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Re-test reminder banner */}
        {["due_soon", "overdue"].includes(user.recert_status) && (
          <div className="rounded-2xl border p-5 mb-8 flex items-start gap-4" style={{ background: rc.bg, borderColor: rc.border }} data-testid="recert-reminder">
            <AlertTriangle className="h-6 w-6 shrink-0 mt-0.5" style={{ color: rc.color }} />
            <div>
              <h3 className="font-bold" style={{ color: rc.color }}>
                {user.recert_status === "overdue" ? "Your RoSPA re-test is overdue" : "Your RoSPA re-test is due soon"}
              </h3>
              <p className="text-sm text-slate-700 mt-1">
                Your re-certification {user.recert_status === "overdue" ? "was due on" : "is due on"}{" "}
                <strong>{fmt(user.recert_date)}</strong>
                {user.days_to_recert != null && (user.days_to_recert < 0
                  ? ` (${Math.abs(user.days_to_recert)} days ago)`
                  : ` (in ${user.days_to_recert} days)`)}.
                Please arrange your re-test with the club to keep your grade current. We'll also email you a reminder.
              </p>
              <Button asChild size="sm" className="mt-3 bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold">
                <Link to="/contact" data-testid="recert-book-btn">Arrange my re-test</Link>
              </Button>
            </div>
          </div>
        )}

        {/* status cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
          <Card icon={Award} label="Current RoSPA grade" data-testid="member-grade">
            <span className="text-xl font-bold capitalize" style={{ color: GRADE_COLOR[user.grade] || "#94A3B8" }}>
              {user.grade === "none" || !user.grade ? "Not yet graded" : user.grade}
            </span>
          </Card>
          <Card icon={CalendarCheck} label="Date last passed" data-testid="member-date-passed">
            <span className="text-lg font-bold text-[#0F2744]">{fmt(user.date_passed)}</span>
          </Card>
          <Card icon={RefreshCw} label="Re-certification date" data-testid="member-recert-date">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg font-bold text-[#0F2744]">{fmt(user.recert_date)}</span>
              <Badge className="text-white" style={{ background: rc.color }}>{rc.label}</Badge>
            </div>
            {user.days_to_recert != null && user.recert_date && (
              <p className="text-xs text-slate-500 mt-1">
                {user.days_to_recert < 0 ? `${Math.abs(user.days_to_recert)} days overdue` : `${user.days_to_recert} days remaining`}
              </p>
            )}
          </Card>
          <Card icon={User} label="Your tutor" data-testid="member-tutor">
            <span className="text-lg font-bold text-[#0F2744]">{user.tutor_assigned || "To be assigned"}</span>
          </Card>
          <MembershipCard user={user} />
          <Card icon={Calendar} label="Booked events" data-testid="member-booking-count">
            <span className="text-xl font-bold text-[#0F2744]">{bookings.filter((b) => b.status === "booked").length}</span>
          </Card>
        </div>

        {/* Grade history */}
        <div className="rounded-2xl bg-white border border-slate-200 p-6 mb-8" data-testid="grade-history">
          <div className="flex items-center gap-2 mb-4">
            <History className="h-5 w-5 text-amber-500" />
            <h2 className="font-bold text-[#0F2744] text-lg">Your RoSPA grade history</h2>
          </div>
          {history.length === 0 ? (
            <p className="text-slate-500">No grades recorded yet. Your passes will appear here once your tutor logs them.</p>
          ) : (
            <div className="space-y-3">
              {history.map((h) => (
                <div key={h.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-xl border border-slate-100 bg-slate-50 p-4" data-testid={`grade-entry-${h.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: GRADE_COLOR[h.grade] || "#94A3B8" }}>
                      <Award className="h-5 w-5 text-white" />
                    </span>
                    <div>
                      <div className="font-bold text-[#0F2744] capitalize">{h.grade}</div>
                      <div className="text-sm text-slate-500">Passed {fmt(h.date)}</div>
                    </div>
                  </div>
                  <div className="text-sm text-slate-500 sm:text-right">
                    Re-certification due <span className="font-semibold text-[#0F2744]">{fmt(addYears(h.date, 3))}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0F2744]" /></div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-8">
            {/* bookings */}
            <div>
              <h2 className="font-bold text-[#0F2744] mb-4 text-lg">My bookings</h2>
              {bookings.length === 0 ? (
                <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center">
                  <p className="text-slate-500 mb-4">You haven't booked any events yet.</p>
                  <Button asChild className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold"><Link to="/events">Browse events</Link></Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {bookings.map((b) => (
                    <div key={b.id} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`booking-${b.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <Badge className={b.status === "booked" ? "bg-green-100 text-green-700 hover:bg-green-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>{b.status === "booked" ? "Confirmed" : "Waiting list"}</Badge>
                          <h3 className="mt-2 font-bold text-[#0F2744]">{b.event?.title || "Event"}</h3>
                          {b.event && (
                            <div className="mt-2 space-y-1 text-sm text-slate-500">
                              <div className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {fmt(b.event.date)} {b.event.time && `· ${b.event.time}`}</div>
                              <div className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {b.event.location}</div>
                            </div>
                          )}
                          {b.event && b.status === "booked" && (
                            <button onClick={() => addToCalendar(b)} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700" data-testid={`ics-${b.id}`}>
                              <CalendarPlus className="h-4 w-4" /> Add to calendar
                            </button>
                          )}
                        </div>
                        <Button onClick={() => cancel(b.id)} size="icon" variant="ghost" className="text-slate-400 hover:text-red-600" data-testid={`cancel-booking-${b.id}`}><X className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* documents */}
            <div>
              <h2 className="font-bold text-[#0F2744] mb-4 text-lg">Club documents</h2>
              <div className="space-y-3">
                <GuideCard compact slug="member" title="Member Guide (PDF)" description="How to use your Member Area, renew, book events and use Community Chat." path="/me/guides/member" />
                {docs.length === 0 && <p className="text-slate-500">No documents available yet.</p>}
                {docs.map((d) => (
                  <div key={d.id} className="rounded-2xl bg-white border border-slate-200 p-5 flex items-start gap-4" data-testid={`document-${d.id}`}>
                    <div className="h-11 w-11 rounded-xl bg-blue-100 flex items-center justify-center shrink-0"><FileText className="h-6 w-6 text-blue-600" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-[#0F2744]">{d.title}</h3>
                        <Badge variant="outline" className="text-xs">{d.category}</Badge>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{d.description}</p>
                    </div>
                    {d.storage_path ? (
                      <button onClick={() => downloadDoc(d)} className="text-slate-400 hover:text-[#0F2744]" aria-label="Download" data-testid={`download-doc-${d.id}`}><Download className="h-5 w-5" /></button>
                    ) : (
                      <span className="text-xs text-slate-400 shrink-0">Coming soon</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <MemberExtras />
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, children, ...rest }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" {...rest}>
      <div className="flex items-center gap-2 text-sm text-slate-500 mb-3"><Icon className="h-4 w-4 text-amber-500" /> {label}</div>
      {children}
    </div>
  );
}

function MembershipCard({ user }) {
  const { t } = useContent();
  const [busy, setBusy] = useState(false);
  const until = user.membership_paid_until;
  const days = until ? Math.ceil((new Date(until) - new Date()) / 86400000) : null;
  const dueSoon = days != null && days <= 30;
  const renew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/membership/renew/checkout");
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.error("Couldn't start renewal. Please try again.");
      setBusy(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="member-membership-card">
      <div className="flex items-center gap-2 text-sm text-slate-500 mb-3"><RefreshCw className="h-4 w-4 text-amber-500" /> Annual membership</div>
      {until ? (
        <>
          <div className="text-lg font-bold text-[#0F2744]">Paid until {fmt(until)}</div>
          {days != null && <p className={`text-xs mt-1 ${days < 0 ? "text-red-600" : dueSoon ? "text-amber-600" : "text-slate-500"}`}>
            {days < 0 ? `${Math.abs(days)} days overdue` : `${days} days remaining`}</p>}
        </>
      ) : (
        <div className="text-sm text-slate-500">Renew any time to keep your membership active.</div>
      )}
      <Button onClick={renew} disabled={busy} size="sm" className={`mt-3 font-bold ${dueSoon || !until ? "bg-amber-500 hover:bg-amber-600 text-[#0F2744]" : "bg-[#0F2744] hover:bg-[#0B192C] text-white"}`} data-testid="member-renew-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Renew membership · ${t("fee.renewal", "£20")}`}
      </Button>
    </div>
  );
}

function DashHeader({ onLogout, unreadTotal = 0, isAdmin = false, canMessage = false }) {
  return (
    <header className="bg-[#0F2744] sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/rospa-logo.png" alt="Rospa Advanced Drivers and Riders" className="h-9 w-9 object-contain" />
          <span className="text-white font-heading font-bold">Member Area</span>
        </Link>
        <div className="flex items-center gap-1 sm:gap-2">
          {isAdmin && <Button asChild variant="ghost" className="text-amber-300 hover:bg-white/10 px-2 sm:px-3"><Link to="/admin" data-testid="member-adminview-btn"><LayoutDashboard className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Admin console</span></Link></Button>}
          {canMessage && (
            <Button asChild variant="ghost" className="text-slate-200 hover:bg-white/10 px-2 sm:px-3"><Link to="/messaging" data-testid="member-messaging-btn" title="Email members"><Mail className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Email</span></Link></Button>
          )}
          <Button asChild variant="ghost" className="relative text-slate-200 hover:bg-white/10 px-2 sm:px-3"><Link to="/member/community" data-testid="member-community-btn"><MessageCircle className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Community</span>
            {unreadTotal > 0 && <span data-testid="community-unread-badge" className="ml-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-[#0F2744] text-xs font-bold flex items-center justify-center">{unreadTotal}</span>}
          </Link></Button>
          <Button asChild variant="ghost" className="text-slate-200 hover:bg-white/10 px-2 sm:px-3"><Link to="/" data-testid="member-home-btn"><Home className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Site</span></Link></Button>
          <Button asChild variant="ghost" className="text-slate-200 hover:bg-white/10 px-2 sm:px-3"><Link to="/profile" data-testid="member-profile-btn"><User className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Your Profile</span></Link></Button>
          <Button onClick={onLogout} variant="ghost" className="text-slate-200 hover:bg-white/10 px-2 sm:px-3" data-testid="member-logout-btn"><LogOut className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Sign out</span></Button>
        </div>
      </div>
    </header>
  );
}
